import formHelper from 'scoreboard/components/form-helper'
import _ from 'lodash'
import RecordUtil from 'scoreboard/datasets/records/record-util'
const { isSupplementalOnly } = RecordUtil
import isNumericDataType from 'scoreboard/components/is-numeric-data-type'
import isGeographicOrRollup from 'scoreboard/datasets/is-geographic-or-rollup'
import numericEvaluator from 'scoreboard/components/evaluators/numeric-evaluator'
import moveDecimal from 'scoreboard/components/move-decimal'
import { isJunctionLinkField } from '../dataset-field-filter'
import createDatasetRecord from 'scoreboard/datasets/model/dataset-record'
import DataType from 'scoreboard/datasets/model/data-type'
const { PERCENT, DATASET_LINK } = DataType

/**
 * Performs a specific Form action
 *
 * @property {ActorOptions} options
 * @property {FormView} formView
 * @property {Form} model
 */
class Actor {

	/**
	 * @typedef ActorOptions
	 *
	 * @property {Form} model
	 *
	 * @property {FormView} formView
	 *
	 * @property {FormAction} action the action to be performed
	 *
	 * @property {DatasetRecord} [record] an existing record we're modifying, if
	 * appropriate (can be omitted for new records)
	 *
	 * @property {FormDirector} formDirector
	 */

	/**
	 * Performs the "Save Record" action
	 *
	 * @constructs
	 *
	 * @param {ActorOptions} options
	 * @param {Form} options.model
	 * @param {FormView} options.formView
	 * @param {FormDirector} options.formDirector
	 */
	constructor(options = {}) {
		this.options = options
		this.model = options.model
		this.formView = options.formView
	}

	/**
	 * Returns whether after executing this actor we should wait for an
	 * associated busy button to finish its checkmark animation
	 */
	isDisplayingDoneCheckmark() { return false }

	/**
	 * Performs the action
	 * @abstract
	 */
	act() {

	}

	maintainFieldValueFromWidget(widget, widgetsView, fieldDataType) {
		if (widget.type === 'field' && widget.state === 'editable') {
			const fromDom = formHelper.readFromDom(widgetsView?.el)
			if (!_.isEmpty(fromDom) && (widget.fieldId in fromDom)) {
				// #53483 be careful not to use falsy test, because the value
				// could be 0
				let value = fromDom[widget.fieldId] ?? null
				// #55961 when changing from editable to text-only, we need to
				// preserve null for blank Geographic/Rollup values
				if (isGeographicOrRollup(fieldDataType)) {
					value = _.isEmpty(value) ? null : value
				}
				this.options.formDirector.setFieldValue(widget.fieldId,
					value)
			}
		}
	}

	/**
	 * Meant to be overridden by subclasses that need to do something to the raw data from the DOM
	 */
	readFromDom() {
		return formHelper.readFromDom(this.formView.el)
	}

	/**
	 * For a given dataset fieldId, returns the index of that field in the record
	 * @param {number} fieldId
	 * @return {number}
	 */
	getRecordFieldIndex (fieldId) {
		return _.findIndex(this._getRecordFields(),
				f => f.id === fieldId)
	}

	getData() {
		const record = this.options.formDirector.record
		const previousRecord = this.options.formDirector.getPreviousRecord()
		// #60252 originalRecord gets mutated later in this function (see calls
		// to originalRecord.push()), so we need to clone it here so subsequent
		// calls to getData() have the same "originalRecord"
		const originalRecord = _.cloneDeep(this.options.formDirector.getOriginalRecord())
		const fieldValues = this.options.formDirector.getFieldValues()
		const fromDom = this.readFromDom()
		const editedByFieldId = this.getEditedByFieldId()

		const linkedRecords = record ? record.linkedRecords : []
		let datasetRecord = createDatasetRecord({
			autogeneratedId: _.isEmpty(previousRecord) ?
				null : previousRecord.autogeneratedId,
			record: [],
			originalRecord,
			edited: [],
			new: this.recordIsNew(),
			insert: this.recordIsInsert(),
			generatedKey: _.get(record, 'generatedKey')
		})

		_.forEach(this._getRecordFields(), (field, index) => {
			let value
			let edited = _.get(record, 'edited.' + index)
			let underlyingField = this.model.dataset.getUnderlyingField(field)

			// Blowup trying to save new record when primary key is a number field
			if (_.has(fromDom, field.id)) {
				value = fromDom[field.id]
				if (_.has(editedByFieldId, field.id)) {
					edited = editedByFieldId[field.id]
				}
				if (isGeographicOrRollup(field.dataType) && !_.isNil(value)) value = _.parseInt(value)
			} else if (!_.isNil(fieldValues[field.id]) ||
				isGeographicOrRollup(underlyingField?.dataType)
			) {
				// #56223 blank text-only geographic/rollup fields aren't read
				// from the dom and have their values stored as null, so we need
				// to preserve this value to reflect its state properly
				value = fieldValues[field.id]
			} else {
				// Check for the original record first because that will have
				// been updated from the last edit
				value = originalRecord && originalRecord[index]
					? originalRecord[index]
					: _.get(record, 'record.' + index)
			}
			if (_.isNil(edited)) {
				edited = false
			}
			// $41273 Linked fields are dropdowns and can return an array for
			// the value, so don't try to evaluate it
			const isLinkedField = field.dataType === DATASET_LINK ||
				isJunctionLinkField(field, this.model.dataset)

			if (isLinkedField && linkedRecords[field.id]) {
				// #45890 Add the original linked field values to the original
				// record so we know if records have been remove
				// Note: we need to pad with nulls so the original value index
				// aligns with its respective field id
				while (datasetRecord.originalRecord.length < index) datasetRecord.originalRecord.push(null)
				// Convert to a string list so it matches the format that we
				// got from the DOM
				const strList = _.map(record.linkedRecords[field.id], f => String(f))
				datasetRecord.originalRecord.push(strList)
			}

			if (!isLinkedField && underlyingField && isNumericDataType(underlyingField.dataType)
				&& !_.isEmpty(value)) {
				// #37485 field's value may be read from the select and unevaluated
				// so will need to evaluate numeric fields here
				value = numericEvaluator(value)

				if (underlyingField.dataType === PERCENT) {
					value = moveDecimal(value, -2)
				}
			}

			// #39967 If this is the manuallyAddedField and this is a
			// manually added record, indicate as much
			let manuallyAddedFieldId =
				_.get(this.model, 'dataset.attributes.manuallyAddedFieldId')
			if (underlyingField.id == manuallyAddedFieldId
				&& isSupplementalOnly(record)) {
				value = true
			}

			datasetRecord.record.push(value)
			datasetRecord.edited.push(edited)
		})
		return datasetRecord
	}

	/**
	 * @returns {boolean} whether the record is manually added (suupplementalonly = true in DB)
	 */
	recordIsNew() {
		const record = this.options.formDirector.record
		return isSupplementalOnly(record)
	}

	/**
	 * @returns {boolean} whether the record is being inserted for the first time
	 */
	recordIsInsert() {
		const record = this.options.formDirector.record
		const originalRecord = this.options.formDirector.getOriginalRecord()
		return _.isEmpty(_.get(record, 'record')) && _.isEmpty(originalRecord)
	}

	/**
	 * #45496 get all datasets fields for the record request
	 */
	_getRecordFields() {
		return _.clone(this.model.dataset.combinedFields)
	}

	/**
	 * @returns {{number: boolean}} the edited flag by field id
	 */
	getEditedByFieldId() {
		const fromDom = this.readFromDom()
		const isNew = this.recordIsNew()
		const isInsert = this.recordIsInsert()
		const editedByFieldId = this._getEditedFromDom()
		_.each(this.formView.$('.field-value-control'), fvc => {
			const fieldEl = this.formView.$(fvc)
			const id = fieldEl.data('id')
			const recordValue = fromDom[id]
			// #56448 if this is a manually added record that's being inserted for
			// the first time, then we want to mark all fields that have a value
			// as edited
			if (isNew && isInsert && !_.isNil(recordValue)) {
				editedByFieldId[id] = true
				return
			}

			const editedFromDom = editedByFieldId[id]
			if (!_.isNil(editedFromDom)) return

			// default to the value from the record that was passed in with options if
			// we didn't get an edited value from the DOM
			if (_.isNil(this.options.formDirector.record)) return

			const record = this.options.formDirector.record
			const fields = this._getRecordFields()
			const index = _.findIndex(fields, f => f.id === id)
			const edited = _.get(record, 'edited.' + index)
			if (_.isNil(edited)) return

			editedByFieldId[id] = edited
		})

		return editedByFieldId
	}

	/**
	 * @returns {{number: boolean}}
	 */
	_getEditedFromDom() {
		const editedFromDom = {}
		_.each(this.formView.$('.field-value-control'), fvc => {
			const fieldEl = this.formView.$(fvc)
			const id = fieldEl.data('id')
			const edited = fieldEl.find('.edited-undo').data('edited')
			if (_.isNil(id) || _.isNil(edited)) return

			editedFromDom[id] = edited
		})
		return editedFromDom
	}
}

export default Actor

Actor.__filename__ = 'forms/actions/actor.js'
